import { CS_BASELINE_GRADER, type EvalRunTask } from '@sbc/domain-models/agents';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ApiError } from '@/lib/api/client';
import { useCancelEvalRun, useEvalRun } from '@/lib/api/eval-runs';
import { Transcript } from '@/routes/sessions/transcript';

import { EvalRunStatusBadge } from './eval-run-status-badge';
import { formatUsd, ReplaySummary } from './replay-summary';
import { RunBox } from './run-box';
import { fromCandidate } from './run-options';

type TagFilter = 'all' | 'capability' | 'regression';

export function RunDetailPage({ runId }: { runId: string }) {
  const runQuery = useEvalRun(runId);
  const cancelRun = useCancelEvalRun();
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<TagFilter>('all');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [rerunOpen, setRerunOpen] = useState(false);

  if (runQuery.isPending) {
    return <p className="text-sm">Loading…</p>;
  }
  if (runQuery.isError) {
    return (
      <p className="text-sm text-[var(--color-destructive)]">
        Failed to load run: {runQuery.error.message}
      </p>
    );
  }

  const run = runQuery.data;
  const running = run.status === 'running';
  const tasks = run.tasks.filter((task) => filter === 'all' || task.suiteTag === filter);
  const misses = run.tasks.filter((task) => !task.passed).length;
  const rerunSuiteId = !running && misses > 0 ? run.suiteId : null;

  const toggle = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });

  const cancel = () => {
    setError(null);
    cancelRun.mutate(runId, {
      onError: (caught) =>
        setError(caught instanceof ApiError ? caught.message : 'Could not cancel the run.'),
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight">{run.suiteName}</h1>
          <div className="flex flex-wrap items-center gap-2 text-sm text-[var(--color-muted-foreground)]">
            <EvalRunStatusBadge status={run.status} />
            <Badge variant="outline">{run.source}</Badge>
            <span>{run.agentModel}</span>
            <span>·</span>
            <span>
              judge {run.judgeProvider}/{run.judgeModel}
            </span>
            {run.label && (
              <>
                <span>·</span>
                <span>{run.label}</span>
              </>
            )}
          </div>
          {run.rerunOf && (
            <p className="text-sm text-[var(--color-muted-foreground)]">
              Re-run of{' '}
              <Link
                to="/evals/runs/$runId"
                params={{ runId: run.rerunOf }}
                className="hover:underline"
              >
                run {run.rerunOf.slice(0, 8)}
              </Link>
              {run.skippedTasks > 0 &&
                ` · ${run.skippedTasks} task(s) skipped because they were deleted from the suite`}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          {running && (
            <Button
              variant="outline"
              onClick={cancel}
              disabled={cancelRun.isPending}
              className="text-[var(--color-destructive)]"
            >
              {cancelRun.isPending ? 'Canceling…' : 'Cancel run'}
            </Button>
          )}
          {rerunSuiteId && !rerunOpen && (
            <Button variant="outline" onClick={() => setRerunOpen(true)}>
              Re-run misses
            </Button>
          )}
          <Button variant="outline" asChild>
            <Link to="/evals/runs">Back</Link>
          </Button>
        </div>
      </div>

      {error && <p className="text-sm text-[var(--color-destructive)]">{error}</p>}
      {run.error && (
        <p className="text-sm text-[var(--color-destructive)]">Run error: {run.error}</p>
      )}

      {rerunSuiteId && rerunOpen && (
        <RunBox
          suiteId={rerunSuiteId}
          rerunOf={run.id}
          title="Re-run misses"
          description={`Scores only the ${misses} failed task(s) of this run again, on the same suite.`}
          submitLabel={`Re-run ${misses} miss(es)`}
          initialLabel={run.label ? `${run.label} (misses)` : ''}
          initialOptions={fromCandidate(run.candidate)}
          onClose={() => setRerunOpen(false)}
        />
      )}

      {running && (
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm tabular-nums">
              Running… {run.taskDone}/{run.taskTotal} tasks complete
            </p>
          </CardContent>
        </Card>
      )}

      {run.summary && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Summary</CardTitle>
          </CardHeader>
          <CardContent>
            <dl className="grid grid-cols-2 gap-2 text-sm sm:grid-cols-3">
              <div>
                <dt className="text-[var(--color-muted-foreground)]">Passed</dt>
                <dd className="tabular-nums">
                  {run.summary.passed}/{run.summary.total}
                </dd>
              </div>
              {run.summary.byTag.map((tag) => (
                <div key={tag.suite}>
                  <dt className="text-[var(--color-muted-foreground)]">{tag.suite}</dt>
                  <dd className="tabular-nums">
                    {tag.passed}/{tag.total}
                  </dd>
                </div>
              ))}
              <div>
                <dt className="text-[var(--color-muted-foreground)]">Avg tokens (in/out)</dt>
                <dd className="tabular-nums">
                  {run.summary.avgTokensIn} / {run.summary.avgTokensOut}
                </dd>
              </div>
              <div>
                <dt className="text-[var(--color-muted-foreground)]">Avg latency</dt>
                <dd className="tabular-nums">{run.summary.avgDurationMs} ms</dd>
              </div>
              {run.summary.avgCostUsd !== undefined && (
                <div>
                  <dt className="text-[var(--color-muted-foreground)]">Avg cost per draft</dt>
                  <dd className="tabular-nums">{formatUsd(run.summary.avgCostUsd)}</dd>
                </div>
              )}
            </dl>
          </CardContent>
        </Card>
      )}

      <ReplaySummary run={run} />

      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-4">
          <CardTitle className="text-base">Tasks</CardTitle>
          <Select
            value={filter}
            onValueChange={(value) =>
              setFilter(
                value === 'capability'
                  ? 'capability'
                  : value === 'regression'
                    ? 'regression'
                    : 'all',
              )
            }
          >
            <SelectTrigger className="h-8 w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All tags</SelectItem>
              <SelectItem value="capability">capability</SelectItem>
              <SelectItem value="regression">regression</SelectItem>
            </SelectContent>
          </Select>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Key</TableHead>
                  <TableHead>Tag</TableHead>
                  <TableHead>Result</TableHead>
                  <TableHead>Trials</TableHead>
                  <TableHead>Tokens</TableHead>
                  <TableHead>Latency</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tasks.length ? (
                  tasks.map((task) => (
                    <TaskRows
                      key={task.id}
                      task={task}
                      expanded={expanded.has(task.id)}
                      onToggle={() => toggle(task.id)}
                    />
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} className="h-24 text-center">
                      {running ? 'Waiting for results…' : 'No tasks.'}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function TaskRows({
  task,
  expanded,
  onToggle,
}: {
  task: EvalRunTask;
  expanded: boolean;
  onToggle: () => void;
}) {
  const failing = task.outcomes.filter(
    (outcome) => !outcome.pass && outcome.grader !== CS_BASELINE_GRADER,
  );
  const csReply = task.outcomes.find((outcome) => outcome.grader === CS_BASELINE_GRADER);
  return (
    <>
      <TableRow className="cursor-pointer" onClick={onToggle}>
        <TableCell className="font-medium">{task.taskKey}</TableCell>
        <TableCell>
          <Badge variant="secondary">{task.category ?? task.suiteTag}</Badge>
        </TableCell>
        <TableCell>
          <Badge variant={task.passed ? 'secondary' : 'destructive'}>
            {task.passed ? 'pass' : 'fail'}
          </Badge>
        </TableCell>
        <TableCell className="tabular-nums">
          {task.trials ? `${task.trials.passed}/${task.trials.total}` : '—'}
        </TableCell>
        <TableCell className="text-sm tabular-nums">
          {task.tokensIn} / {task.tokensOut}
          {task.costUsd !== null && ` · ${formatUsd(task.costUsd)}`}
        </TableCell>
        <TableCell className="text-sm tabular-nums">{task.durationMs} ms</TableCell>
      </TableRow>
      {expanded && (
        <TableRow>
          <TableCell colSpan={6} className="bg-[var(--color-muted)]">
            <div className="space-y-4 py-2">
              {failing.length > 0 && (
                <div className="space-y-1">
                  <p className="text-sm font-medium">Failing graders</p>
                  {failing.map((outcome) => (
                    <p
                      key={`${outcome.grader}-${outcome.reason}`}
                      className="text-sm text-[var(--color-destructive)]"
                    >
                      {outcome.grader}: {outcome.reason}
                    </p>
                  ))}
                </div>
              )}
              {csReply && (
                <p className="text-sm">
                  CS reply {csReply.pass ? 'passed' : 'failed'}: {csReply.reason}
                </p>
              )}
              <Transcript messages={task.transcript} />
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}
